import React, { ReactElement, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { gray100, gray40, gray80, orange50 } from '../../../constants/colors';
import { regularXl, regularSm } from '../../../constants/location';
import { usageType } from '../../../constants/enum';
import { OcrCreditUsageType } from '../../../types/ocrCreditUsage';
import { getOcrCreditUsageHistory } from '../../../api/firebase/firestore/ocrCreditUsage/read';

type Props = {
  open: boolean;
  onClose: () => void;
  companyId: string;
  showNote?: boolean;
};

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      borderRadius: '8px',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '32px 32px 0 32px',
    '@media screen and (max-width:960px)': {
      padding: '16px 16px 0 16px',
    },
  },
  title: {
    ...regularXl,
    color: gray100,
  },
  iconButton: {
    padding: 0,
  },
  icon: {
    color: gray80,
    height: '32px',
    width: '32px',
  },
  content: {
    padding: '24px 32px 32px 32px',
    overflow: 'auto',
    '@media screen and (max-width:960px)': {
      padding: '16px',
    },
  },
  tableContainer: {
    maxHeight: '400px',
    border: `1px solid ${gray40}`,
    borderRadius: '4px',
  },
  tableHead: {
    backgroundColor: gray40,
    '& th': {
      ...regularSm,
      fontWeight: '600',
      color: gray100,
      padding: '12px 16px',
      whiteSpace: 'nowrap',
    },
  },
  tableBody: {
    '& td': {
      ...regularSm,
      color: gray100,
      padding: '12px 16px',
      whiteSpace: 'nowrap',
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
  },
  amountPositive: {
    color: '#2E7D32',
    fontWeight: '500',
  },
  amountNegative: {
    color: orange50,
    fontWeight: '500',
  },
  expiresAt: {
    ...regularSm,
    color: gray80,
    fontSize: '11px',
  },
  noteCell: {
    maxWidth: '280px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  noData: {
    textAlign: 'center',
    padding: '40px',
    color: gray80,
  },
};

const getUsageTypeLabel = (type: number): string => {
  switch (type) {
    case usageType.ocrUsage:
      return 'OCR消費';
    case usageType.purchase:
      return '購入';
    case usageType.manualGrant:
      return '付与';
    case usageType.adjustment:
      return '調整';
    case usageType.expiration:
      return '失効';
    case usageType.referral:
      return 'リファラル特典';
    case usageType.coupon:
      return 'クーポン特典';
    default:
      return '不明';
  }
};

const formatDate = (date: Date): string => {
  return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
};

const OcrCreditUsageHistoryModal = ({
  open,
  onClose,
  companyId,
  showNote = true,
}: Props): ReactElement => {
  const [creditHistory, setCreditHistory] = useState<OcrCreditUsageType[]>([]);

  useEffect(() => {
    if (!open || !companyId) {
      return;
    }

    const fetchHistory = async (): Promise<void> => {
      try {
        const history = await getOcrCreditUsageHistory(companyId);
        setCreditHistory(history);
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    fetchHistory();
  }, [open, companyId]);

  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>クレジット履歴</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>

      <Box sx={styles.content}>
        {creditHistory.length === 0 ? (
          <Typography sx={styles.noData}>履歴がありません</Typography>
        ) : (
          <TableContainer sx={styles.tableContainer}>
            <Table stickyHeader size="small">
              <TableHead sx={styles.tableHead}>
                <TableRow>
                  <TableCell>日付</TableCell>
                  <TableCell>種別</TableCell>
                  <TableCell>金額</TableCell>
                  <TableCell>残高</TableCell>
                  {showNote && <TableCell>備考</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody sx={styles.tableBody}>
                {creditHistory.map(record => (
                  <TableRow key={record.id}>
                    <TableCell>{formatDate(record.created_at)}</TableCell>
                    <TableCell>{getUsageTypeLabel(record.usage_type)}</TableCell>
                    <TableCell
                      sx={record.amount >= 0 ? styles.amountPositive : styles.amountNegative}
                    >
                      {record.amount >= 0 ? '+' : ''}
                      {record.amount.toLocaleString()}円
                    </TableCell>
                    <TableCell>{record.after_balance.toLocaleString()}円</TableCell>
                    {showNote && (
                      <TableCell sx={styles.noteCell}>
                        <Tooltip
                          title={record.note}
                          arrow
                          placement="top-start"
                          disableInteractive={false}
                        >
                          <span>{record.note}</span>
                        </Tooltip>
                        {record.expires_at && (
                          <Typography sx={styles.expiresAt}>
                            有効期限: {formatDate(record.expires_at)}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Dialog>
  );
};

export default OcrCreditUsageHistoryModal;
